/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import {
  Hidden,
  Box,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  LinearProgress,
  Pagination,
  Stack,
  Grid,
  Typography,
  Theme,
  Skeleton,
  Collapse,
  IconButton,
} from '@mui/material';
import { ChangeEvent } from 'react';
import { SxProps } from '@mui/system';
import { parseIfJson, truncateText } from '../../utils/utilityFunctions';
import { documentTypeNameMap, TRUNCATE_TEXT_LENGTH } from '../../utils/constant';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import NomineeDetails from '../investor-management/individual/view/nomineeDetails';
import BankDetails from '../investor-management/individual/view/BankDetails';
import { useModal } from 'mui-modal-provider';
import SimpleDialog from '../commonComponents';
import { UBO } from '../investor-management/non-individual/view/ubo';
import { AuthorizedSignatory } from '../investor-management/non-individual/view/AuthorisedSignatories';
import { ControllingPersonReadOnly } from '../investor-management/non-individual/form/fatca_components';

/**
 * @param header - Column header as a string or a function returning a React node.
 * @param label - Optional label to extract value from the row.
 * @param value - Optional value to render in the cell (string, number, boolean, or undefined).
 * @function valueGetter - Computes the cell value.
 *                  `(row: any)` => `string | number | boolean | undefined`
 * @param isSwitch - Optional flag indicating if the cell is a switch component.
 * @function switchAction - Triggered when the switch is toggled.
 *                  `(id: string)` => `void`
 * @function renderCell - Custom rendering for a cell.
 *                  `(row: any)` => `React.ReactNode`
 * @function renderEditDeleteCell - Renders edit/delete actions in a cell.
 *                  `(row: any, index: number, groupIndex?: number)` => `React.ReactNode`
 * @param truncate_text - Optional flag to truncate text in the cell.
 */

export type ColumnType = {
  /**
   * Header of the column
   */
  header: string | (() => React.ReactNode);

  /**
   * To get the value from the row
   */
  label?: string;

  /**
   * Value to render in the table
   */
  value?: string | number | boolean | undefined;

  /**
   * Function to get the value from the row
   */
  valueGetter?: (row: any, index: number) => string | number | boolean | undefined;

  /**
   * Boolean to check if the cell is switch
   */
  isSwitch?: boolean;

  /**
   * Action for switch component
   */
  switchAction?: (id: string) => void;

  /**
   * Render custom cell
   */
  renderCell?: (row: any) => React.ReactNode;

  /**
   * Renders edit/delete actions in a cell
   */
  renderEditDeleteCell?: (row: any, index: number, groupIndex?: number) => React.ReactNode;

  /**
   * Boolean flag to truncate large text
   */
  truncate_text?: boolean;
  isExpandableIcon?: boolean;
  displayGetter?: (row: any, index: number, showModal: any) => React.ReactNode; // for UI
};

export const linkStyle: SxProps<Theme> = {
  background: 'none',
  border: 'none',
  padding: 0,
  marginLeft: '8px', // little gap after bankName
  cursor: 'pointer',
  font: 'inherit',
  color: 'primary.main', // theme link color
  fontWeight: 500,
  textDecoration: 'none',
  '&:hover': {
    textDecoration: 'underline',
    color: 'primary.dark', // slightly darker on hover
  },
  '&:focus': {
    outline: 'none',
    textDecoration: 'underline',
  },
};

export const renderCellValue = (label: string, row: any, showModal: any) => {
  const value = parseIfJson(row[label]);
  // if (['oldValue', 'newValue'].includes(label)) {
  //   console.log(label, row, row[label]);
  // }
  const isEmpty = (val: any) => {
    if (val === null || val === undefined) return true;
    if (typeof val === 'string' && val.trim() === '') return true;
    if (Array.isArray(val) && val.length === 0) return true;
    if (typeof val === 'object' && Object.keys(val).length === 0) return true;
    return false;
  };

  // Special cases for nominee/bank fields
  if (['oldValue', 'newValue'].includes(label)) {
    if (/nominee/i.test(row.entityType)) {
      return typeof value === 'string' ? (
        value
      ) : (
        <>
          {value.nomineeName}
          <Box
            sx={linkStyle}
            component={'button'}
            onClick={() => {
              const { destroy } = showModal(SimpleDialog, {
                content: (
                  <NomineeDetails
                    nominees={Array.isArray(value) ? value : value ? [value] : []}
                    updateScreen={true}
                  />
                ),
                actions: [
                  {
                    label: 'OK',
                    onClick: () => {
                      destroy();
                    },
                  },
                ],
              });
            }}>
            ( View )
          </Box>
        </>
      );
    }
    if (/bank/i.test(row.entityType)) {
      return typeof value === 'string' ? (
        value
      ) : (
        <>
          {value.bankName}
          <br />
          (A/C No: {value.bankAccountNumber})
          <br />
          <Box
            sx={linkStyle}
            component={'button'}
            onClick={() => {
              const { destroy } = showModal(SimpleDialog, {
                content: (
                  <BankDetails
                    banks={Array.isArray(value) ? value : value ? [value] : []}
                    updateScreen={true}
                  />
                ),
                actions: [
                  {
                    label: 'OK',
                    onClick: () => {
                      destroy();
                    },
                  },
                ],
              });
            }}>
            ( View )
          </Box>
        </>
      );
    }
    if (Object.values(documentTypeNameMap).includes(row.field)) {
      return typeof value === 'string' ? (
        value
      ) : (
        <>
          {value.documentName}
          <br />
          {value?.viewUrl && (
            <Box sx={linkStyle} component={'button'} onClick={() => window.open(value?.viewUrl)}>
              ( View )
            </Box>
          )}
        </>
      );
    }
    if (/group[\s_]*signatory/i.test(row.entityType)) {
      return typeof value === 'string' ? (
        value
      ) : (
        <>
          {value.name || ''}
          <Box
            sx={linkStyle}
            component={'button'}
            onClick={() => {
              const { destroy } = showModal(SimpleDialog, {
                content: (
                  <AuthorizedSignatory signatory={Array.isArray(value) ? value[0] : value} />
                ),
                actions: [
                  {
                    label: 'OK',
                    onClick: () => {
                      destroy();
                    },
                  },
                ],
              });
            }}>
            ( View )
          </Box>
        </>
      );
    }
    if (/ubo/i.test(row.entityType)) {
      return typeof value === 'string' ? (
        value
      ) : (
        <>
          {value.name || ''}
          <Box
            sx={linkStyle}
            component={'button'}
            onClick={() => {
              const { destroy } = showModal(SimpleDialog, {
                content: (
                  <UBO
                    ubos={Array.isArray(value) ? value : value ? [value] : []}
                    // updateScreen={true}
                  />
                ),
                actions: [
                  {
                    label: 'OK',
                    onClick: () => {
                      destroy();
                    },
                  },
                ],
              });
            }}>
            ( View )
          </Box>
        </>
      );
    }
    if (/controlling[\s_]*person/i.test(row.entityType)) {
      return typeof value === 'string' ? (
        value
      ) : (
        <>
          {value.name || ''}
          <Box
            sx={linkStyle}
            component={'button'}
            onClick={() => {
              const { destroy } = showModal(SimpleDialog, {
                content: (
                  <ControllingPersonReadOnly
                    controllingPersonsArr={Array.isArray(value) ? value : value ? [value] : []}
                    // updateScreen={true}
                  />
                ),
                actions: [
                  {
                    label: 'OK',
                    onClick: () => {
                      destroy();
                    },
                  },
                ],
              });
            }}>
            ( View )
          </Box>
        </>
      );
    }
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    if (isEmpty(value)) return '—';
    // Default case: never return a raw object
    if (typeof value === 'object') return JSON.stringify(value); // or extract a key here
  }
  if (isEmpty(value)) return '—';
  return value;
};

function MobileChildTable({
  tableHeader,
  tableData,
  groupIndex,
}: {
  tableHeader: ColumnType[];
  tableData: any;
  groupIndex: number;
}) {
  return (
    <Box
      sx={{
        border: '1px solid #ccc',
        borderRadius: 1,
        p: 2,
        bgcolor: '#f5f5f5',
        mt: 1,
        mb: 2,
      }}>
      {tableData?.map((row: any, index: number) => (
        <Grid
          container
          spacing={1}
          key={index}
          sx={{
            border: '1px solid #00D9DA',
            p: 1.25,
            borderRadius: 1.25,
            mb: 1.25,
            rowGap: 1.25,
            padding: { xs: '10px', sm: '55px' },
            '.MuiGrid-item': {
              px: 1,
            },
            '.MuiTypography-root': {
              fontSize: { xs: 10, sm: 16 },
              fontWeight: 500,
              maxWidth: '204px',
              overflowWrap: 'anywhere',
            },
            '& .MuiTypography-h6': {
              fontFamily: 'Poppins, sans-serif',
              color: 'primary.main',
            },
            '& .MuiTypography-body1': {
              color: 'rgba(41, 49, 57, 0.7)',
            },
          }}>
          {tableHeader?.map((col, idx) => (
            <Grid item xs={6} key={idx}>
              <Typography variant="body2" color="textSecondary">
                {typeof col.header === 'string' ? col.header : col.header()}
              </Typography>
              <Typography variant="body1">
                {col.renderCell ? (
                  col.renderCell(row)
                ) : col.renderEditDeleteCell ? (
                  col.renderEditDeleteCell(row, index, groupIndex)
                ) : (
                  <Typography>
                    {col.valueGetter ? col.valueGetter(row, index) : row[col.label ?? '']}
                  </Typography>
                )}
              </Typography>
            </Grid>
          ))}
        </Grid>
      ))}
    </Box>
  );
}

export function MobileViewDataRow({
  row,
  index,
  groupIndex,
  tableHeader,
  isLoading = false,
  isExpanded,
  onToggleExpand,
  childTableHeader,
}: {
  row: any;
  index: number;
  groupIndex?: number;
  tableHeader: ColumnType[];
  isLoading?: boolean;
  isExpanded?: boolean; // flag to expand row
  onToggleExpand?: () => void; // set row to be expanded
  childTableHeader?: ColumnType[];
}): JSX.Element {
  const { showModal } = useModal();

  return (
    <>
      <Grid
        key={index}
        container
        // columnSpacing={1}
        sx={{
          border: '1px solid #00D9DA',
          p: 1.25,
          // ml: 0,
          borderRadius: 1.25,
          mb: 1.25,
          rowGap: 1.25,
          padding: { xs: '10px', sm: '55px' },
          '.MuiGrid-item': {
            px: 1,
          },
          '.MuiTypography-root': {
            fontSize: { xs: 10, sm: 16 },
            fontWeight: 500,
            maxWidth: '204px',
            overflowWrap: 'anywhere',
          },
          '& .MuiTypography-h6': {
            fontFamily: 'Poppins, sans-serif',
            color: 'primary.main',
          },
          '& .MuiTypography-body1': {
            color: 'rgba(41, 49, 57, 0.7)',
          },
        }}>
        {tableHeader?.map((headerList, idx) => {
          const {
            valueGetter,
            // eslint-disable-next-line
            isSwitch,
            label = '',
            renderCell,
            header,
            renderEditDeleteCell,
            isExpandableIcon,
            displayGetter,
          } = headerList;
          return (
            <Grid item xs={6} sm={4} key={idx}>
              {typeof header !== 'string' ? (
                header()
              ) : (
                <Typography variant="h6">{headerList.header}</Typography>
              )}
              {isLoading ? (
                <Skeleton />
              ) : // isSwitch ? (
              //   <Stack
              //     sx={{
              //       '.MuiSwitch-root': { paddingLeft: '0px', width: '52px' },
              //       '.MuiSwitch-switchBase': { paddingLeft: '0px' },
              //     }}>
              //     <Switch
              //       sx={{
              //         '.MuiSwitch-switchBase': { color: 'black' },
              //       }}
              //       checked={row[label]}
              //       onChange={() => headerList.switchAction && headerList.switchAction(row.id)}
              //     />
              //   </Stack>
              // ) :
              isExpandableIcon ? (
                <Box
                  component="span"
                  onClick={(e: React.MouseEvent<HTMLSpanElement>) => {
                    e.stopPropagation();
                    onToggleExpand && onToggleExpand();
                  }}
                  sx={{
                    color: 'primary.main',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    display: 'inline-block',
                    fontSize: 12,
                  }}>
                  View Application
                </Box>
              ) : renderCell ? (
                renderCell(row)
              ) : renderEditDeleteCell ? (
                renderEditDeleteCell(row, index, groupIndex)
              ) : (
                <Typography>
                  {displayGetter
                    ? displayGetter(row, index, showModal)
                    : valueGetter
                    ? valueGetter(row, index)
                    : renderCellValue(label, row, showModal)}
                </Typography>
              )}
            </Grid>
          );
        })}
      </Grid>
      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <MobileChildTable
          tableHeader={childTableHeader || []}
          tableData={row.applications}
          groupIndex={Number(groupIndex)}
        />
      </Collapse>
    </>
  );
}

export function DataRow({
  row,
  index,
  rowCustomStyles,
  groupIndex,
  currentPage,
  tableHeader,
  isExpanded,
  onToggleExpand,
  childTableHeader,
  childTableData,
}: {
  row: any;
  index: number;
  rowCustomStyles?: SxProps<Theme>;
  groupIndex?: number;
  currentPage?: number;
  tableHeader: ColumnType[];
  isExpanded: boolean; // flag to expand row
  onToggleExpand: () => void; // set row to be expanded
  childTableHeader: ColumnType[];
  childTableData: any;
}): JSX.Element {
  const { showModal } = useModal();

  return (
    <>
      <TableRow
        key={index}
        // onClick={onToggleExpand}
        sx={{
          background: currentPage ? '#FAFAFB' : '',
          ':hover': {
            bgcolor: currentPage ? '#FAFAFB' : '',
          },
          ...rowCustomStyles,
        }}>
        {tableHeader.map((header, ind) => {
          const {
            valueGetter,
            // eslint-disable-next-line
            isSwitch,
            renderCell,
            label = '',
            renderEditDeleteCell,
            truncate_text = false,
            isExpandableIcon = false,
            displayGetter,
          } = header;
          return (
            <TableCell key={ind} align="center">
              {/* {isSwitch ? (
              <Stack alignItems={'center'}>
                <Switch
                  sx={{ '.MuiSwitch-switchBase': { color: 'black' } }}
                  checked={row.isActive}
                  // onChange={() => header.switchAction && header.switchAction(row.id)}
                />
              </Stack>
            ) :  */}
              {isExpandableIcon ? (
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation(); // ✅ prevent event from bubbling to row click
                    onToggleExpand();
                  }}>
                  {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              ) : displayGetter ? (
                displayGetter(row, index, showModal)
              ) : valueGetter ? (
                truncate_text ? ( // truncate length if truncate_text flag is true
                  truncateText(valueGetter(row, index) as string, TRUNCATE_TEXT_LENGTH)
                ) : (
                  valueGetter(row, index)
                )
              ) : renderCell ? (
                renderCell(row)
              ) : renderEditDeleteCell ? (
                renderEditDeleteCell(row, index, groupIndex)
              ) : (
                renderCellValue(label, row, showModal)
              )}
            </TableCell>
          );
        })}
      </TableRow>

      <TableRow>
        <TableCell colSpan={tableHeader.length} sx={{ padding: 0 }}>
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Box sx={{ padding: 3, bgcolor: '#EEE' }}>
              <DataTable
                isLoading={false}
                tableHeader={childTableHeader}
                tableData={childTableData || []}
                boxStyles={{ bgcolor: '#AAA', minHeight: 'fit-content', borderRadius: 2 }}
                rowCustomStyles={{
                  '&:last-child td, &:last-child th': { border: 0 },
                }}
              />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export function WebViewDataRow({
  rowCustomStyles,
  renderAdditionalRow = false,
  tableBodyCustomStyles,
  tableHeaderCustomStyles,
  groupIndex,
  currentPage,
  tableHeader,
  tableData,
  rowExpandable,
  childTableHeader,
}: {
  rowCustomStyles?: SxProps<Theme>;
  currentPage?: number;
  renderAdditionalRow?: boolean;
  tableBodyCustomStyles?: SxProps<Theme>;
  tableHeaderCustomStyles?: SxProps<Theme>;
  groupIndex?: number;
  tableHeader: ColumnType[];
  tableData: any;
  rowExpandable?: boolean; // rowExpandable flag will expand rows of a table
  childTableHeader?: ColumnType[];
}): JSX.Element {
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  return (
    <TableContainer sx={{ bgcolor: 'common.white' }}>
      <Table aria-label="Application table">
        <TableHead>
          <TableRow
            sx={{
              '.MuiTableCell-head': {
                fontFamily: 'Poppins, sans-serif',
                fontSize: 12,
                color: 'primary.main',
              },
              ...tableHeaderCustomStyles,
            }}>
            {tableHeader.map((currHeader: ColumnType, index: number) => {
              const { header } = currHeader;
              return (
                <TableCell
                  key={'h' + index}
                  align="center"
                  sx={{
                    whiteSpace: 'nowrap',
                  }}>
                  {typeof header !== 'string' ? header() : currHeader['header']}
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody
          sx={{
            '.MuiTableRow-root': {
              cursor: currentPage ? 'pointer' : 'auto',
              ':hover': {
                bgcolor: currentPage ? '#F4FCFC' : '',
              },
            },
            ...tableBodyCustomStyles,
          }}>
          {tableData?.length ? (
            tableData?.map((row: any, index: number) => (
              <React.Fragment key={index}>
                <DataRow
                  groupIndex={groupIndex}
                  index={index}
                  row={row}
                  key={index}
                  rowCustomStyles={rowCustomStyles}
                  currentPage={currentPage}
                  tableHeader={tableHeader}
                  isExpanded={Boolean(rowExpandable) && expandedRow === index}
                  onToggleExpand={
                    () => rowExpandable && setExpandedRow((prev) => (prev === index ? null : index)) // set index for row to make expanded
                  }
                  childTableHeader={childTableHeader || []}
                  childTableData={row.applications}
                />
                {renderAdditionalRow && (
                  <TableRow
                    sx={{
                      '.MuiTableCell-root': {
                        padding: '4px',
                      },
                    }}>
                    <TableCell colSpan={tableHeader.length}></TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={tableHeader.length}
                sx={{
                  textAlign: 'center',
                  height: 100,
                }}>
                {'No data found'}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export function DataTable({
  isLoading,
  tableData,
  tableHeader,
  rowCustomStyles,
  noOfPages,
  currentPage,
  paginationAction,
  renderAdditionalRow = false,
  tableBodyCustomStyles,
  tableHeaderCustomStyles,
  groupIndex,
  boxStyles,
  rowExpandable,
  childTableHeader,
}: {
  isLoading?: boolean;
  tableData: any;
  tableHeader: ColumnType[];
  rowCustomStyles?: SxProps<Theme>;
  noOfPages?: number;
  currentPage?: number;
  paginationAction?: (event: ChangeEvent<unknown>, value: number) => void;
  renderAdditionalRow?: boolean;
  tableBodyCustomStyles?: SxProps<Theme>;
  tableHeaderCustomStyles?: SxProps<Theme>;
  groupIndex?: number;
  boxStyles?: SxProps<Theme>;
  rowExpandable?: boolean; // rowExpandable flag will expand rows of a table
  childTableHeader?: ColumnType[];
}): JSX.Element {
  tableData = isLoading ? new Array(5).fill({}) : tableData;
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  return (
    <>
      <Hidden only="xs">
        <Box
          sx={{
            maxHeight: 'calc(100vh - 400px)',
            overflow: 'auto',
            minHeight: '480px',
            minWidth: 650,
            ...boxStyles,
          }}>
          {!isLoading ? (
            <WebViewDataRow
              rowCustomStyles={rowCustomStyles}
              currentPage={currentPage}
              renderAdditionalRow={renderAdditionalRow}
              tableBodyCustomStyles={tableBodyCustomStyles}
              tableHeaderCustomStyles={tableHeaderCustomStyles}
              groupIndex={groupIndex}
              tableHeader={tableHeader}
              tableData={tableData}
              rowExpandable={rowExpandable}
              childTableHeader={childTableHeader || []}
            />
          ) : (
            <LinearProgress />
          )}
        </Box>
      </Hidden>
      <Hidden smUp={true}>
        {tableData?.length ? (
          tableData?.map((row: any, index: number) => (
            <MobileViewDataRow
              index={index}
              row={row}
              key={index}
              tableHeader={tableHeader}
              isLoading={isLoading}
              isExpanded={Boolean(rowExpandable) && expandedRow === index}
              onToggleExpand={
                () => rowExpandable && setExpandedRow((prev) => (prev === index ? null : index)) // set index for row to make expanded
              }
              childTableHeader={childTableHeader || []}
            />
          ))
        ) : (
          <Typography
            sx={{
              textAlign: 'center',
              height: 100,
            }}>
            {'No data found'}
          </Typography>
        )}
      </Hidden>
      {!!tableData?.length && !!currentPage && !isLoading && (
        <Stack direction="row" justifyContent="center" sx={{ mt: 5 }}>
          <Pagination
            count={noOfPages}
            page={currentPage}
            color="primary"
            onChange={paginationAction}
          />
        </Stack>
      )}
    </>
  );
}
